/**
 * Per-account billing for the provider cost-efficiency report: a subscription price or "billed per
 * token". Blank means unknown (the default). A price is financial detail the hub has no need to
 * hold, so it never enters hub-visible settings: encrypted accounts keep it in a sealed `setting`
 * entity synced across trusted devices (g13-digests follow-up); otherwise on this device only.
 */
import { SettingEntry, settingEntityId } from '@viko/protocol'
import { z } from 'zod'
import type { BrowserEntities } from './encrypted-entities.js'
import type { Billing } from './provider-efficiency.js'

const KEY = 'viko.providerBilling.v1'
const Entry = z.union([
  z.strictObject({
    kind: z.literal('subscription'),
    monthlyUsd: z.number().positive().max(100_000),
  }),
  z.strictObject({ kind: z.literal('api') }),
])
const Store = z.record(z.string().min(1).max(160), Entry)

export function loadBilling(
  storage: Pick<Storage, 'getItem'> | undefined = safe(),
): Record<string, Billing> {
  try {
    const raw = storage?.getItem(KEY)
    if (!raw) return {}
    const parsed = Store.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : {}
  } catch {
    return {}
  }
}

/** `undefined` clears the entry (billing unknown again). Returns the stored map. */
export function saveBilling(
  accountId: string,
  billing: Billing | undefined,
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined = safe(),
): Record<string, Billing> {
  const next = { ...loadBilling(storage) }
  if (billing && Entry.safeParse(billing).success) next[accountId] = billing
  else delete next[accountId]
  try {
    storage?.setItem(KEY, JSON.stringify(next))
  } catch {
    /* storage blocked: the choice lasts for this session only */
  }
  return next
}

function safe(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage
  } catch {
    return undefined
  }
}

// ---- g13-digests: encrypted, synced billing (moved from this device's localStorage) ----
type Entities = Pick<BrowserEntities, 'list' | 'write'>

async function readSynced(entities: Entities): Promise<Record<string, Billing> | undefined> {
  const id = settingEntityId('providerBilling')
  const row = (await entities.list('setting')).find((r) => r.id === id)
  if (!row) return undefined
  const entry = SettingEntry.safeParse(row.value)
  if (!entry.success || entry.data.name !== 'providerBilling') return {}
  const parsed = Store.safeParse(entry.data.value)
  return parsed.success ? parsed.data : {}
}

async function writeSynced(entities: Entities, value: Record<string, Billing>, now = Date.now()) {
  await entities.write(
    'setting',
    settingEntityId('providerBilling'),
    SettingEntry.parse({ name: 'providerBilling', value: Store.parse(value), updatedAt: now }),
  )
}

/**
 * The synced prices. The first load on a device that still has local prices (and no synced
 * entity yet) moves them into the entity; the local copy is removed once the entity exists.
 */
export async function loadSyncedBilling(
  entities: Entities,
  storage: Pick<Storage, 'getItem' | 'removeItem'> | undefined = safe(),
): Promise<Record<string, Billing>> {
  const synced = await readSynced(entities)
  const local = loadBilling(storage)
  let out = synced
  if (!out) {
    out = local
    if (Object.keys(local).length) await writeSynced(entities, local)
  }
  try {
    storage?.removeItem(KEY)
  } catch {
    /* storage blocked: nothing to clean up */
  }
  return out
}

/** Set one account's billing on the latest synced value (other devices' edits are kept). */
export async function saveSyncedBilling(
  entities: Entities,
  accountId: string,
  billing: Billing | undefined,
): Promise<Record<string, Billing>> {
  const next = { ...((await readSynced(entities)) ?? {}) }
  if (billing && Entry.safeParse(billing).success) next[accountId] = billing
  else delete next[accountId]
  await writeSynced(entities, next)
  return next
}
// ---- end g13-digests ----
